import type { State as BooksState } from '../store/books/reducer'
import type { State as DepthOfMarketState } from '../store/depthOfMarket/reducer'
import { getDepthForSecurity } from '../store/depthOfMarket/selectors'
import { getUserOrderKey } from '../store/order/helpers'
import type { State as OrderState } from '../store/order/reducer'
import { getUserOrdersBySecurityId } from '../store/order/selectors'
import type { Order } from '../store/order/types'
import type { State as SecuritiesState } from '../store/securities/reducer'
import {
  getSecurityOrderDataById,
  getSecurityStaticDataById
} from '../store/securities/selectors'
import type { State } from '../store/types'
import type { State as WebSettingsState } from '../store/webSettings/reducer'
import { HydrationPayload } from './hydration'

/* store/types State resolves to any (globalSettingsReducer is typed Reducer<any>),
   so the builders declare the slices they actually read. */
type HydrationState = {
  books: BooksState
  depthofmarket: DepthOfMarketState
  order: OrderState
  securities: SecuritiesState
  webSettings: WebSettingsState
}

type HydrationBuilder = (
  state: HydrationState,
  params: string[]
) => HydrationPayload

const entry = <T>(id: number, value: T | undefined): Record<number, T> => {
  const result: Record<number, T> = {}
  if (value !== undefined) result[id] = value
  return result
}

const byOrderKey = (orders: Order[]): Record<string, Order> => {
  const result: Record<string, Order> = {}
  for (const order of orders) result[getUserOrderKey(order)] = order
  return result
}

/* Aggressor's on-mount openAggressorWindow rebuilds its window + temp order locally
   from the hydrated depth, so carrying the ladder saves it a round-trip. */
const buildSecurityModalHydration = (
  state: HydrationState,
  securityId: number
): HydrationPayload => {
  /* getDepthForSecurity yields an empty ladder rather than undefined, so entry()
     needs the explicit check to represent absence the way the caches above do. */
  const depth = getDepthForSecurity(state)(securityId)
  return {
    securities: {
      securitiesStaticDataInfoCache: entry(
        securityId,
        getSecurityStaticDataById(state)(securityId)
      ),
      securitiesOrderDataInfoCache: entry(
        securityId,
        getSecurityOrderDataById(state)(securityId)
      )
    },
    depthofmarket: {
      depthBySecurity: entry(securityId, depth.length ? depth : undefined)
    }
  }
}

/* Aggressor/{orderId}/{securityId} — the security is the second param, not the first. */
const Aggressor: HydrationBuilder = (state, [, securityId]) =>
  buildSecurityModalHydration(state, Number(securityId))

/* webSettings + books gate OrderEntry and its book-select footer; userOrders gates the
   Cancel button in BuyOrSellDepth, which otherwise paints an empty action cell. */
const Depth: HydrationBuilder = (state, [securityIdParam]) => {
  const securityId = Number(securityIdParam)
  return {
    ...buildSecurityModalHydration(state, securityId),
    books: state.books,
    webSettings: state.webSettings,
    order: {
      userOrders: byOrderKey(getUserOrdersBySecurityId(state)[securityId] ?? [])
    }
  }
}

const hydrationBuilders: Record<string, HydrationBuilder> = {
  Aggressor,
  Depth
}

export const buildHydration = (
  name: string,
  state: State
): HydrationPayload | undefined => {
  const [baseName, ...params] = name.split('/')
  return hydrationBuilders[baseName]?.(state as HydrationState, params)
}
